import { useEffect } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { Bars3Icon, XMarkIcon, SunIcon, MoonIcon, InboxIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom';
import { getInitials } from '../../utils/userUtils';
import { useUser } from '../../context/UserContext';
import { useTheme } from '../../context/ThemeContext';
import { useProjects } from '../../context/ProjectsContext';

export default function Navbar({ onLogout, mobileSidebarOpen = false, onMobileSidebarToggle }) {
  const { user } = useUser();
  const { isDarkMode, toggleTheme } = useTheme();
  const { incomingCount, refreshIncomingCount } = useProjects();

  // Poll the invitations badge every 60s. Cheap call (returns array of pending invites).
  useEffect(() => {
    refreshIncomingCount();
    const interval = setInterval(refreshIncomingCount, 60_000);
    return () => clearInterval(interval);
  }, [refreshIncomingCount]);

  return (
    <div className="sticky top-0 z-50">
      <div className="w-full bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700/50 px-4 py-2 flex items-center justify-center gap-2">
        <span className="text-amber-500 text-sm">⚠</span>
        <p className="text-amber-700 dark:text-amber-400 text-sm font-medium">
          BETA: This application is currently in beta testing for internal users only.
        </p>
      </div>
<nav className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 transition-colors duration-300">
      <div className="w-full px-4">
        <div className="relative flex h-16 items-center justify-between">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4">
            {/* Hamburger button: toggles mobile drawer on small screens, collapses/expands desktop sidebar on larger screens.*/}
            <button
              type="button"
              onClick={onMobileSidebarToggle}
              className="relative inline-flex items-center justify-center rounded-md p-2 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-indigo-500 transition-colors"
              aria-label={mobileSidebarOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileSidebarOpen}
              aria-controls="mobile-sidebar"
            >
              {/* Invisible overlay that extends the clickable area by 2px in all directions */}
              <span className="absolute -inset-0.5" />
              {mobileSidebarOpen ? (
                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
              ) : (
                <Bars3Icon aria-hidden="true" className="h-6 w-6" />
              )}
            </button>
          </div>
          <div className="flex flex-1 items-center justify-center md:hidden">
            <span className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white transition-colors">
              Logio
            </span>
          </div>
          <div className="hidden md:block flex-1" />
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 md:static md:inset-auto md:ml-6 md:pr-0 gap-2">
            {/* Invitations */}
            <Link
              to="/invitations"
              className="relative p-1.5 rounded-full text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="Invitations"
              aria-label="Invitations"
            >
              <InboxIcon className="size-5" />
              {incomingCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-rose-600 rounded-full ring-2 ring-white dark:ring-slate-800">
                  {incomingCount > 99 ? '99+' : incomingCount}
                </span>
              )}
            </Link>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-full text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? (
                <SunIcon className="size-5" />
              ) : (
                <MoonIcon className="size-5" />
              )}
            </button>
              
              {/* Profile dropdown */}
              <Menu as="div" className="relative ml-3">
                <MenuButton className="relative flex rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500">
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">Open user menu</span>
                  <div className={`size-8 rounded-full flex items-center justify-center text-white font-bold text-xs ring-2 ring-white/10 ${!user ? 'animate-pulse bg-slate-400' : 'bg-indigo-600'}`}>
                    {user ? getInitials(user?.displayName) : '...'}
                  </div>
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/10 py-1 shadow-lg outline-none ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="px-4 py-2 border-b border-slate-100 dark:border-white/5">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Signed in as</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.email || 'Loading...'}</p>
                  </div>
                  <MenuItem>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-300 data-[focus]:bg-slate-50 data-[focus]:text-slate-900 dark:data-[focus]:bg-white/5 dark:data-[focus]:text-white transition-colors"
                    >
                      Profile Settings
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={onLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 data-[focus]:bg-slate-50 data-[focus]:text-slate-900 dark:data-[focus]:bg-white/5 dark:data-[focus]:text-white transition-colors"
                    >
                      Sign out
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </div>
          </div>
        </div>
      </nav>
    </div>
  )
}
